// Table des routes publiques — module pur (Phase 96, D-08 / D-09 / D-10).
//
// Source UNIQUE du `noindex`, des `hreflang` et du sitemap. Deux contraintes la
// structurent, et aucune des deux n'est cosmétique :
//
//   · ZÉRO import, jamais. Ce module est importé par `webapp/vite.config.ts`
//     (plan 96-07, génération du sitemap au build) : un seul import de React,
//     de CSS, d'un asset ou même de `./types` ferait entrer du code applicatif
//     dans la chaîne de configuration de Vite et casserait le bundling. Le
//     contrôle `routes.test.ts` l'assère par `grep`-équivalent.
//
//   · Les `path` sont RECOPIÉS LITTÉRALEMENT depuis `src/main.tsx`, jamais
//     écrits de mémoire. Un `path` faux ne casse rien de visible : il fait
//     simplement qu'aucun canonical n'est émis pour la page, **en silence**.
//     C'est la pire classe de défaut de cette phase.
//
// L'administration est délibérément absente : elle reste française (D-08).
// Refs: phase 96.

export type PublicRoute = {
  /** Chemin NU, sans préfixe de langue — copie littérale du `path` de main.tsx. */
  path: string
  /**
   * Basename du module de page sous `src/pages/`, sans extension.
   * `null` pour une redirection pure, qui n'a aucun contenu propre.
   */
  module: string | null
  /**
   * Faux ⟹ la jumelle `/en/` porte `noindex`, n'a pas de `hreflang` et n'entre
   * pas au sitemap (D-09). La Phase 97 lève le drapeau page par page, et le
   * contrôle 2 de `routes.test.ts` fait échouer la suite si on l'oublie.
   */
  translated: boolean
  /** Faux ⟹ ni sitemap ni indexation, dans AUCUNE langue. */
  indexable: boolean
}

export const PUBLIC_ROUTES = [
  { path: '/', module: 'Home', translated: true, indexable: true },
  // Phase 99 plan 99-07 — LA BASCULE. Dernière route du périmètre à lever son
  // `noindex` : à partir d'ici, PLUS AUCUNE route de cette table n'est
  // `indexable && !translated`.
  //
  // Ce que ça a coûté, pour que la prochaine personne ne cherche pas : les
  // quatre témoins SEO qui vivaient sur cette classe d'entrée
  // (`seoLinks.test.ts`, `SeoHead.test.tsx`) ont perdu leur sujet de PRODUCTION
  // et vivent désormais sur une route-fixture synthétique passée en paramètre à
  // `seoLinks()` (plan 99-02, D-04). Ils n'ont été ni supprimés ni relâchés —
  // la classe d'entrée reviendra dès qu'une page sera lancée en français
  // d'abord.
  { path: '/mentions-legales', module: 'MentionsLegales', translated: true, indexable: true },
  // D-12 — une page de connexion indexée n'apporte rien et n'abîme rien ;
  // l'exclure demanderait une justification que personne n'aura en mémoire.
  { path: '/login', module: 'Login', translated: true, indexable: true },
  // Phase 97 plan 97-06 — non indexable, donc le drapeau ne lève aucun
  // noindex : il ARME le harnais D-04 en permanence sur cette page.
  { path: '/auth/callback', module: 'AuthCallback', translated: true, indexable: false },
  { path: '/dashboard', module: 'Dashboard', translated: true, indexable: false },
  { path: '/packs', module: 'Packs', translated: true, indexable: true },
  // Phase 97 plan 97-05 — la COQUILLE du blog est traduite (libellés, états,
  // dates). Le contenu éditorial des articles reste servi tel quel : c'est de
  // la donnée, et la Phase 100 en porte le bilinguisme.
  { path: '/blog', module: 'Blog', translated: true, indexable: true },
  { path: '/blog/:slug', module: 'BlogArticle', translated: true, indexable: false },
  // Traduite au plan 97-07, ses TROIS états compris (formulaire, envoyé, jeton
  // invalide) — les deux derniers par des cas d'état riche du harnais D-04.
  { path: '/feedback/:token', module: 'Feedback', translated: true, indexable: false },
  // 97-08 — traduite dans ses DEUX états : le formulaire (cas de `CASES`) et la
  // page d'erreur (cas d'état riche). Ses messages d'erreur de paiement viennent
  // désormais du catalogue et non plus de l'Edge Function.
  { path: '/packs/checkout/:size', module: 'PackCheckout', translated: true, indexable: false },
  { path: '/purchase/success', module: 'PurchaseSuccess', translated: true, indexable: false },
  {
    path: '/reserve/:duration_hours',
    module: 'ReserveSession',
    translated: true,
    indexable: false,
  },
  // 97-09 — traduite dans ses TROIS états : disponible (cas de `CASES`),
  // paiement en attente et offre déjà utilisée (deux cas d'état riche).
  { path: '/reserve/trial', module: 'ReserveTrial', translated: true, indexable: false },
  // 97-09 — un seul état (la page est une coquille au-dessus de SlotPicker,
  // déjà traduit) : le cas de `CASES` le couvre entièrement.
  {
    path: '/reserve/coaching-perso',
    module: 'ReserveCoachingPerso',
    translated: true,
    indexable: false,
  },
  // Traduite au plan 97-07. Non indexable (page de compte), donc le drapeau ne
  // lève ici aucun `noindex` : il arme le cas D-04 du harnais.
  { path: '/mon-compte', module: 'MonCompte', translated: true, indexable: false },
  // Phase 109 (FACT-01). Bilingue dès sa création — son catalogue page-local
  // existe avant elle (plan 109-03). Non indexable (page de compte), donc
  // `translated` ne lève ici aucun `noindex` : il arme le cas D-04 du harnais.
  { path: '/mes-factures', module: 'MesFactures', translated: true, indexable: false },
  // Redirection pure vers /session/checkout — aucun contenu propre, donc
  // `module: null` : rien à traduire, rien à indexer.
  { path: '/seance-unitaire', module: null, translated: false, indexable: false },
  { path: '/session/checkout', module: 'SessionCheckout', translated: true, indexable: true },
  {
    path: '/session/purchase/success',
    module: 'SessionPurchaseSuccess',
    translated: true,
    indexable: false,
  },
  // Phase 97 plan 97-06 — CINQUIÈME page indexable du périmètre à lever son
  // noindex. Elle laissait alors `/mentions-legales` seule en
  // `indexable && !translated` ; la Phase 99 l'a soldée (voir plus haut).
  { path: '/install', module: 'Install', translated: true, indexable: true },
  // 97-09 — DERNIÈRE page du périmètre de la phase. Traduite dans ses quatre
  // états ; le cas de `CASES` monte « en cours », un cas d'état riche monte
  // « confirmé », celui qui porte l'annonce du PIN et les deux CTA.
  {
    path: '/booking/confirmed/:id',
    module: 'SlotConfirmation',
    translated: true,
    indexable: false,
  },
] as const satisfies readonly PublicRoute[]

/**
 * Route correspondant à un chemin NU (déjà dépréfixé de `/en`).
 *
 * Liste blanche par défaut (T-96-01) : un chemin absent de la table rend
 * `undefined`, et les consommateurs SEO doivent le traiter comme non
 * indexable. Jamais de liste noire — un chemin oublié doit tomber du côté sûr.
 *
 * `table` existe pour rendre PURE une fonction qui prétendait déjà l'être : la
 * dérivation SEO doit rester vérifiable sur la classe d'entrée
 * `indexable && !translated`, que la table de production ne porte plus après la
 * Phase 99 (D-04) — et qui reviendra dès qu'une page sera lancée en français
 * d'abord. Le défaut EST la table de production, et c'est structurel : un
 * appelant qui omet l'argument obtient le comportement d'aujourd'hui, jamais
 * une table vide, qui ferait silencieusement tomber TOUTES les pages du côté
 * « absente de la table ⟹ noindex ». Aucun appelant de production ne le passe.
 * Refs: phase 99.
 */
export function findRoute(
  barePath: string,
  table: readonly PublicRoute[] = PUBLIC_ROUTES,
): PublicRoute | undefined {
  return table.find((r) => r.path === barePath)
}
